/**
 * Alpine component for the suspension-appeal form (auth/appeal.twig).
 *
 * Tracks the character count for the appeal message and exposes min/max
 * length thresholds so the template can show "N characters remaining" /
 * "N / max" hints. Configuration arrives via a data-config JSON attribute
 * on the host element so this module stays free of Twig interpolation.
 *
 * Usage in Twig:
 *
 *   <form x-data="appealForm"
 *         data-config='{
 *             "messageText": {{ message|json_encode(15)|raw }},
 *             "minLength": {{ min_length }},
 *             "maxLength": {{ max_length }}
 *         }'>
 *     ...
 *   </form>
 */

export function appealForm() {
    return {
        messageText: '',
        minLength: 0,
        maxLength: 0,
        submitting: false,
        recoveryTimer: null,

        init() {
            const raw = this.$root?.dataset?.config;
            if (raw) {
                try {
                    const config = JSON.parse(raw);
                    if (typeof config.messageText === 'string') {
                        this.messageText = config.messageText;
                    }
                    if (Number.isFinite(config.minLength)) {
                        this.minLength = config.minLength;
                    }
                    if (Number.isFinite(config.maxLength)) {
                        this.maxLength = config.maxLength;
                    }
                } catch (_e) {
                    // Bad JSON in data-config: leave defaults; the field still
                    // works, the counter just won't enforce any thresholds.
                }
            }

            // After a failed server-side validation pass, the template marks
            // the first errored input with data-autofocus-error so we can
            // restore focus there.
            const target = this.$root.querySelector('[data-autofocus-error]');
            if (target instanceof HTMLElement) {
                target.focus({ preventScroll: false });
            }

            // The back-forward cache restores live JS state, so navigating
            // back to a submitted appeal form would otherwise return a button
            // still latched into its spinner state and refusing every click.
            // The captcha script resets its own in-flight flag the same way.
            window.addEventListener('pageshow', (event) => {
                if (event.persisted) {
                    this.stopSubmitting();
                }
            });
        },

        get charCount() {
            return (this.messageText || '').length;
        },

        /**
         * Debounce the appeal submission so rapid double or triple taps cannot
         * each fire a POST and create duplicate appeals and feedback rows.
         *
         * This is bound to the submit button's click, deliberately NOT to a
         * form-level Alpine submit directive. Captcha::printCaptcha() refuses
         * to bind its reCAPTCHA v3 token-refresh submit handler on any form
         * that declares @submit / x-on:submit (such a form is expected to own
         * the token refresh itself). A form-level directive here would leave
         * #googleResponse empty and make every non-China appeal POST fail
         * captcha validation on the server.
         *
         * The button is also never given the disabled attribute while a
         * submission is in flight: setting disabled inside the same click that
         * triggers the submit cancels that submit in every major browser. The
         * first click latches `submitting` (showing the spinner) and lets the
         * native submit proceed so the captcha script can refresh the token;
         * every later click is cancelled here instead.
         */
        onSubmitClick(event) {
            if (this.submitting) {
                event.preventDefault();
                return;
            }

            // Latch only when the form will actually submit. checkValidity()
            // is false while a required field is empty, and a click blocked by
            // native validation performs no navigation, so latching there
            // would wedge the button and lock the user out of the form.
            const form = this.$root;
            if (form && typeof form.checkValidity === 'function' && !form.checkValidity()) {
                return;
            }

            this.submitting = true;

            // Safety net for a hung reCAPTCHA. On the Google path the captcha
            // script calls grecaptcha.execute() before it submits; if that
            // promise never settles, the form never navigates and pageshow
            // never fires, so the spinner would stay latched until a manual
            // refresh -- worse than the pre-change behavior where a second tap
            // forced an empty-token POST and a reload. Clearing the latch after
            // a generous delay lets the user retry. The window is deliberately
            // long: a real submission (including a slow multipart upload of
            // attachments) navigates well before it elapses, so this only ever
            // fires when something is genuinely stuck.
            this.stopRecoveryTimer();
            this.recoveryTimer = window.setTimeout(() => {
                this.submitting = false;
                this.recoveryTimer = null;
            }, 15000);
        },

        stopRecoveryTimer() {
            if (this.recoveryTimer !== null) {
                window.clearTimeout(this.recoveryTimer);
                this.recoveryTimer = null;
            }
        },

        stopSubmitting() {
            this.submitting = false;
            this.stopRecoveryTimer();
        },
    };
}
